import { Layout } from "@/components/layout/Layout";
import { SectionHeader } from "@/components/ui/SectionHeader";
import { CheckCircle, XCircle, Clock, Mail, Shield, AlertTriangle, BadgeCheck } from "lucide-react";
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";

export default function RefundPolicy() {
  return (
    <Layout>
      <Helmet>
        <title>Refund Policy — Verified Badge Program | Transparency Badge</title>
        <meta name="description" content="Refund policy for the Verified Badge Program. First month free, no charge if rejected, cancel anytime. Fair billing with no surprises." />
        <link rel="canonical" href="https://transparencybadge.com/refund-policy" />
      </Helmet>

      {/* Hero */}
      <section className="border-b bg-gradient-to-b from-accent/50 to-background py-16 md:py-20">
        <div className="container">
          <SectionHeader
            as="h1"
            badge="Billing & Refunds"
            title="Refund Policy"
            description="We believe in fair, transparent billing. Here is exactly what happens with payments and refunds for the Verified Badge Program."
          />
        </div>
      </section>

      <section className="py-16 md:py-20">
        <div className="container max-w-3xl">

          {/* Summary box */}
          <div className="bg-safe-bg border border-safe/30 rounded-2xl p-8 mb-12">
            <div className="flex items-center gap-3 mb-4">
              <div className="flex h-10 w-10 items-center justify-center rounded-full bg-safe text-safe-foreground">
                <Shield className="h-5 w-5" />
              </div>
              <h2 className="text-xl font-bold text-foreground">Our Commitment</h2>
            </div>
            <p className="text-muted-foreground">
              The Verified Badge Program starts with a <span className="font-semibold text-foreground">free first month</span> — no credit card required to apply. You are only charged after your website passes our eligibility check and your badge is activated. If your site does not pass, you are never charged. We handle all billing fairly and transparently.
            </p>
          </div>

          <div className="space-y-10">

            {/* Free first month */}
            <div>
              <h2 className="text-xl font-bold text-foreground mb-4 flex items-center gap-3">
                <BadgeCheck className="h-5 w-5 text-primary" />
                1. Free First Month
              </h2>
              <div className="bg-card rounded-xl border p-6 space-y-3">
                <p className="text-muted-foreground">
                  The first month of the Verified Badge Program is completely free. No payment is taken upfront when you submit your application.
                </p>
                <ul className="space-y-2">
                  {[
                    "Submit your application at no cost",
                    "We review your website within 1–2 business days",
                    "If approved, your badge activates and the free month begins",
                    "Billing starts only from the second month at $10/month",
                    "Cancel before the second month and you pay absolutely nothing",
                  ].map((item) => (
                    <li key={item} className="flex items-start gap-2 text-sm text-muted-foreground">
                      <CheckCircle className="h-4 w-4 text-safe shrink-0 mt-0.5" />
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>

            {/* Failed review */}
            <div>
              <h2 className="text-xl font-bold text-foreground mb-4 flex items-center gap-3">
                <XCircle className="h-5 w-5 text-primary" />
                2. If Your Application Is Not Approved
              </h2>
              <div className="bg-card rounded-xl border p-6 space-y-3">
                <p className="text-muted-foreground">
                  If your website does not pass our eligibility check, your application is declined and you are not charged anything.
                </p>
                <ul className="space-y-2">
                  {[
                    "No charge if your site is rejected during review",
                    "We will email you explaining why and what can be improved",
                    "You are welcome to reapply after making the necessary changes",
                    "There is no reapplication fee",
                  ].map((item) => (
                    <li key={item} className="flex items-start gap-2 text-sm text-muted-foreground">
                      <CheckCircle className="h-4 w-4 text-safe shrink-0 mt-0.5" />
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>

            {/* Active subscription */}
            <div>
              <h2 className="text-xl font-bold text-foreground mb-4 flex items-center gap-3">
                <Clock className="h-5 w-5 text-primary" />
                3. Active Subscription Refunds
              </h2>
              <div className="bg-card rounded-xl border p-6 space-y-4">
                <p className="text-muted-foreground">
                  Once your badge is active and billing has started, the following refund terms apply:
                </p>
                <div className="bg-muted/50 rounded-lg p-4 space-y-3">
                  <div className="flex items-start gap-3">
                    <CheckCircle className="h-4 w-4 text-safe shrink-0 mt-0.5" />
                    <div>
                      <p className="text-sm font-medium text-foreground">Cancellation at any time</p>
                      <p className="text-sm text-muted-foreground">You may cancel your subscription at any time. Your badge remains active until the end of the current billing period. No further charges are made after cancellation.</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <AlertTriangle className="h-4 w-4 text-caution shrink-0 mt-0.5" />
                    <div>
                      <p className="text-sm font-medium text-foreground">Current billing period</p>
                      <p className="text-sm text-muted-foreground">Payments already processed for the current billing period are generally non-refundable once the badge has been active and serving on your website. This is consistent with standard SaaS subscription terms.</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <CheckCircle className="h-4 w-4 text-safe shrink-0 mt-0.5" />
                    <div>
                      <p className="text-sm font-medium text-foreground">Technical failures on our end</p>
                      <p className="text-sm text-muted-foreground">If your badge stops working due to a technical error on our side and we are unable to resolve it within 72 hours, you are entitled to a pro-rata refund or account credit for the affected days.</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Badge revocation */}
            <div>
              <h2 className="text-xl font-bold text-foreground mb-4 flex items-center gap-3">
                <Shield className="h-5 w-5 text-primary" />
                4. Badge Revocation
              </h2>
              <div className="bg-card rounded-xl border p-6 space-y-3">
                <p className="text-muted-foreground">
                  We continuously monitor verified websites. If we find that a verified website has violated our standards after approval, we reserve the right to revoke the badge immediately.
                </p>
                <ul className="space-y-2">
                  {[
                    "Badge revoked for policy violations — no refund for the current billing period",
                    "Badge revoked for fraudulent information provided during application — no refund",
                    "We will notify you by email when a badge is revoked and explain the reason",
                    "If you believe a revocation was made in error, contact us to appeal",
                  ].map((item) => (
                    <li key={item} className="flex items-start gap-2 text-sm text-muted-foreground">
                      <AlertTriangle className="h-4 w-4 text-caution shrink-0 mt-0.5" />
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>

            {/* Payment failure */}
            <div>
              <h2 className="text-xl font-bold text-foreground mb-4 flex items-center gap-3">
                <Clock className="h-5 w-5 text-primary" />
                5. Payment Failures & Grace Period
              </h2>
              <div className="bg-card rounded-xl border p-6 space-y-3">
                <p className="text-muted-foreground">
                  If a payment fails, we provide a grace period before suspending your badge.
                </p>
                <ul className="space-y-2">
                  {[
                    "3-day grace period after a failed payment before badge is suspended",
                    "You will be notified by email immediately when a payment fails",
                    "You can update your payment method at any time through your billing portal",
                    "Badge is automatically reactivated once payment is successfully processed",
                  ].map((item) => (
                    <li key={item} className="flex items-start gap-2 text-sm text-muted-foreground">
                      <CheckCircle className="h-4 w-4 text-safe shrink-0 mt-0.5" />
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>

            {/* How to request */}
            <div>
              <h2 className="text-xl font-bold text-foreground mb-4 flex items-center gap-3">
                <Mail className="h-5 w-5 text-primary" />
                6. How to Request a Refund
              </h2>
              <div className="bg-card rounded-xl border p-6 space-y-4">
                <p className="text-muted-foreground">
                  To request a refund or raise a billing dispute, contact us directly. We aim to resolve all billing issues fairly and promptly.
                </p>
                <div className="bg-accent/50 rounded-lg p-4 space-y-2">
                  <p className="text-sm font-medium text-foreground">Contact us at:</p>
                  <a href="mailto:hello@transparencybadge.com" className="text-primary hover:underline text-sm font-medium">
                    hello@transparencybadge.com
                  </a>
                  <p className="text-sm text-muted-foreground mt-2">Please include your domain name and a brief description of the issue. We respond to all billing inquiries within 3–5 business days.</p>
                </div>
              </div>
            </div>

            {/* Changes */}
            <div>
              <h2 className="text-xl font-bold text-foreground mb-4">7. Changes to This Policy</h2>
              <div className="bg-card rounded-xl border p-6">
                <p className="text-muted-foreground">
                  We may update this Refund Policy from time to time. Any changes will be posted on this page with an updated date. Continued use of the Verified Badge Program after changes constitutes acceptance of the updated policy. For significant changes, we will notify active subscribers by email.
                </p>
              </div>
            </div>

          </div>

          {/* CTA */}
          <div className="mt-12 text-center">
            <p className="text-sm text-muted-foreground mb-4">Related legal documents:</p>
            <div className="flex flex-wrap justify-center gap-4">
              <Link to="/terms" className="text-sm text-primary hover:underline">Terms of Service</Link>
              <Link to="/privacy" className="text-sm text-primary hover:underline">Privacy Policy</Link>
              <Link to="/get-verified" className="text-sm text-primary hover:underline">Get Verified</Link>
            </div>
          </div>
        </div>
      </section>

      {/* Last Updated */}
      <section className="py-8 border-t">
        <div className="container max-w-3xl text-center">
          <p className="text-sm text-muted-foreground">Last updated: March 2026</p>
        </div>
      </section>
    </Layout>
  );
}
